import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowUpRight, Download, FileText, Loader2, Lock, LogOut, Presentation } from "lucide-react";
import SEO from "@/components/SEO";
import { formatError } from "@/lib/growthApi";
import { portal, clientTokenKey } from "@/lib/portalApi";
import { CALENDLY_URL, CONTACT_EMAIL } from "@/content";

const INPUT = "w-full border border-white/15 bg-transparent px-4 py-3.5 text-center font-mono text-lg tracking-[0.3em] text-white outline-none transition-colors focus:border-gold/60";
const BTN = "inline-flex items-center justify-center gap-2 bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow disabled:opacity-60";
const GHOST = "inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-gold hover:text-gold";

function Unlock({ slug, name, onUnlocked }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const d = await portal.unlock(slug, code); localStorage.setItem(clientTokenKey(slug), d.token); onUnlocked(); }
    catch (ex) { setErr(formatError(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 pt-24" data-testid="portal-unlock-page">
      <form onSubmit={submit} className="w-full max-w-md border border-white/10 bg-navy-light/30 p-8 sm:p-10">
        <p className="eyebrow mb-4">Private client portal</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white" data-testid="portal-client-name">{name}</h1>
        <p className="mt-3 text-sm text-slate-400">Enter the access code provided by Thrive to view your reports.</p>
        <label htmlFor="portal-code" className="mt-8 mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Access code</label>
        <input id="portal-code" data-testid="portal-access-code-input" autoComplete="off" required value={code} onChange={(e) => setCode(e.target.value)} className={INPUT} />
        {err && <p className="mt-4 text-sm text-red-400" data-testid="portal-unlock-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="portal-unlock-button" className={`${BTN} mt-8 w-full`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Unlock reports</button>
        <p className="mt-6 text-center text-xs text-slate-500">Lost your code? Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-slate-300 hover:text-gold" data-testid="portal-lost-code-link">{CONTACT_EMAIL}</a></p>
      </form>
    </div>
  );
}

function ReportCard({ slug, r, active, onPreview }) {
  return (
    <article className={`border p-6 transition-colors ${active ? "border-gold/50 bg-navy-light/40" : "border-white/10 bg-navy-light/20 hover:border-white/25"}`} data-testid={`portal-report-${r.id}`}>
      {r.report_date && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{r.report_date}</p>}
      <h3 className="mt-2 font-display text-lg font-bold tracking-[-0.02em] text-white">{r.title}</h3>
      {r.description && <p className="mt-2 text-sm text-slate-400">{r.description}</p>}
      <div className="mt-5 flex flex-wrap gap-3">
        {r.pdf_name ? (
          <>
            <button type="button" onClick={() => onPreview(r)} className={GHOST} data-testid={`portal-preview-${r.id}`}><FileText size={14} /> Preview</button>
            <a href={portal.fileUrl(slug, r.id, "pdf")} className={GHOST} data-testid={`portal-download-pdf-${r.id}`}><Download size={14} /> PDF</a>
          </>
        ) : null}
        {r.pptx_name && <a href={portal.fileUrl(slug, r.id, "pptx")} className={GHOST} data-testid={`portal-download-pptx-${r.id}`}><Presentation size={14} /> PowerPoint</a>}
      </div>
    </article>
  );
}

function Reports({ slug, onLocked }) {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    portal.reports(slug).then((d) => { setData(d); const first = d.reports.find((r) => r.pdf_name); if (first) setPreview(first); })
      .catch((e) => { if (e.response?.status === 401) onLocked(); else setErr(formatError(e)); });
  }, [slug, onLocked]);
  if (err) return <p className="pt-40 text-center text-red-400" data-testid="portal-error">{err}</p>;
  if (!data) return <div className="flex min-h-screen items-center justify-center pt-24"><Loader2 className="animate-spin text-gold" /></div>;
  return (
    <div className="pt-28 sm:pt-32 pb-24" data-testid="portal-reports-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-white/10 pb-8">
          <div>
            <p className="eyebrow mb-3">Private client portal</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.03em] text-white" data-testid="portal-reports-heading">{data.name}</h1>
            <p className="mt-3 text-sm text-slate-400">Prepared by Thrive Business Development. Confidential to {data.name}.</p>
          </div>
          <button type="button" onClick={() => { localStorage.removeItem(clientTokenKey(slug)); onLocked(); }} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-gold" data-testid="portal-lock-button"><LogOut size={12} /> Lock portal</button>
        </div>
        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,380px)_1fr]">
          <div className="space-y-4" data-testid="portal-report-list">
            {data.reports.length === 0 && <p className="text-sm text-slate-500" data-testid="portal-empty">No reports have been published yet.</p>}
            {data.reports.map((r) => <ReportCard key={r.id} slug={slug} r={r} active={preview?.id === r.id} onPreview={setPreview} />)}
            <div className="border border-gold/30 bg-navy-light/20 p-6" data-testid="portal-cta">
              <p className="eyebrow mb-3">Next step</p>
              <h3 className="font-display text-lg font-bold text-white">Talk to Thrive</h3>
              <p className="mt-2 text-sm text-slate-400">Discuss the findings, bid implications or next phase of research with us directly.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className={BTN} data-testid="portal-book-call">Book a call <ArrowUpRight size={14} /></a>
                <a href={`mailto:${CONTACT_EMAIL}`} className={GHOST} data-testid="portal-email-link">Email us</a>
              </div>
            </div>
          </div>
          <div className="min-h-[520px] border border-white/10 bg-navy-light/20" data-testid="portal-preview-panel">
            {preview ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="portal-preview-title">Preview · {preview.title}</p>
                  <a href={portal.fileUrl(slug, preview.id, "pdf", "preview")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-gold-glow" data-testid="portal-open-fullscreen">Open full screen <ArrowUpRight size={12} /></a>
                </div>
                <object key={preview.id} data={portal.fileUrl(slug, preview.id, "pdf", "preview")} type="application/pdf" className="h-[75vh] min-h-[520px] w-full" data-testid="portal-pdf-frame" aria-label={preview.title}>
                  <div className="flex h-[520px] flex-col items-center justify-center gap-4 p-10 text-center text-sm text-slate-400" data-testid="portal-preview-fallback">
                    <p>Your browser cannot display PDFs inline.</p>
                    <a href={portal.fileUrl(slug, preview.id, "pdf", "preview")} target="_blank" rel="noreferrer" className={GHOST}>Open the report <ArrowUpRight size={14} /></a>
                  </div>
                </object>
              </>
            ) : <div className="flex h-full items-center justify-center p-10 text-sm text-slate-500">Select a report to preview it here.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [missing, setMissing] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem(clientTokenKey(slug)));
  useEffect(() => { portal.info(slug).then(setInfo).catch(() => setMissing(true)); }, [slug]);
  if (missing) return <div className="pt-40 text-center" data-testid="portal-not-found"><p className="eyebrow">Private client portal</p><h1 className="mt-4 font-display text-2xl font-bold text-white">This portal does not exist.</h1></div>;
  if (!info) return <div className="flex min-h-screen items-center justify-center pt-24"><Loader2 className="animate-spin text-gold" /></div>;
  return (
    <>
      <SEO title={`${info.name} | Client Portal | Thrive`} description="Private client portal." noindex />
      {unlocked ? <Reports slug={slug} onLocked={() => setUnlocked(false)} /> : <Unlock slug={slug} name={info.name} onUnlocked={() => setUnlocked(true)} />}
    </>
  );
}
